<template>
  <div
    v-if="showIcon"
    :class="$style.container"
  >
    <span :class="$style.premium">
      <Icon icon="premiumDiscover" />
    </span>
    <Button
      v-if="hasBillingRole"
      :class="[$style.learnMoreLink, $style.learnMoreLink]"
      data-test-id="tryFeature__learnMoreInternalLink"
      hasNoBackground
      text="Learn About Plan Options"
      @click="handleInternalLinkClick"
    />
    <Button
      v-else
      :class="[$style.learnMoreLink, $style.learnMoreLink]"
      data-test-id="tryFeature__learnMoreExternalLink"
      hasNoBackground
      isExternal
      :outline="false"
      text="Learn About Plan Options"
      type="informational"
      @click="handleExternalLinkClick"
    />
  </div>
</template>

<script>
import { Button, Icon } from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
  PricingLink,
} from 'client/util/Constants';
import {
  eventLocations,
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
// import { trialFeatures } from 'client/util/Constants/FeatureTrialConstants';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import MspState from 'client/components/models/MspState';
import Util from 'client/util/Util';

const planModule = 'PlanModule';
const settingsModule = 'SettingsModule';

const {
  description,
  location,
  section,
  text,
  toUrl,
} = eventProperties;

const externalUrl = PricingLink.pricing;

export default {
  name: 'LearnMoreLink',

  components: {
    Button,
    Icon,
  },

  Util,

  props: {
    featureCode: {
      default: '',
      require: true,
      type: String,
    },
    featureName: {
      default: '',
      require: true,
      type: String,
    },
  },
  computed: {
    ...mapGetters(planModule, [
      'hasPlan',
      'planHasFeature',
    ]),

    ...mapGetters(settingsModule, [
      'hasBilling',
    ]),

    ...mapState(settingsModule, [
      'isCustomer',
    ]),

    hasBillingRole() {
      return this.hasBilling
        && !MspState.isAnMspOrChild;
    },

    showIcon() {
      return !this.planHasFeature(this.featureCode);
    },
  },

  async created() {
    if (this.hasBillingRole) await this.fetchPlan();
  },

  methods: {
    ...mapActions(planModule, ['fetchPlan']),

    handleExternalLinkClick() {
      EventHelper.triggerLinkClicked({
        [description]: 'Opens link to the external pricing page.',
        [location]: eventLocations.settingsPage,
        [section]: `Features You're Trying section - ${this.featureName}`,
        [text]: 'Learn About Plan Options',
        [toUrl]: externalUrl,
      });

      window.open(externalUrl, '_blank');
    },

    handleInternalLinkClick() {
      const pageName = this.hasPlan ? 'upgrade' : 'internal pricing';
      const pricingPageLink = ItemTypeRoutes[ItemTypeConsts.pricing];
      const upgradeTabLink = ItemTypeRoutes[ItemTypeConsts.accountUpgrade];
      EventHelper.triggerLinkClicked({
        [description]: `Opens link to the ${pageName} page.`,
        [location]: `Feature ${eventLocations.settingsPage} page - ${this.featureName} trial`,
        [section]: `Features You're Trying section - ${this.featureName}`,
        [text]: 'Learn About Plan Options',
        [toUrl]: this.hasPlan ? upgradeTabLink : pricingPageLink,
      });

      if (this.hasPlan) {
        this.$router.push({ path: upgradeTabLink });
      } else {
        this.$router.push({ path: pricingPageLink });
      }
    },
  },
};
</script>

<style module scoped>
.container {
  align-items: center;
  display: flex;
  font-size: var(--jcBody);
  gap: var(--jc-spacer-small);
}

.learnMoreLink.learnMoreLink {
  color: var(--jc-color-informational-strong);
}

.premium {
  align-items: center;
  background: var(--jcTertiary50);
  border: var(--jc-border-width) solid var(--jcTertiary50);
  border-radius: var(--jc-border-radius);
  display: flex;
  justify-content: center;
  padding: 0.25em;
}
</style>
